import {Component, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {filter, switchMap, take} from "rxjs/operators";
import {AccountControllerService} from "../../core/api/controllers/account-controller/account-controller.service";
import {IAttemptPaymentRequest} from "../../core/api/controllers/account-controller/interfaces/attempt-payment.request";
import {IAttemptPaymentResponse} from "../../core/api/controllers/account-controller/interfaces/attempt-payment.response";
import {paymentMethodUnavailableMessage, requiresPaymentMethodUpdate} from "../../core/helpers/payment-failure.helper";
import {ShopControllerService} from "../../core/api/controllers/shop-controller.service";
import {SubscriptionUserType} from "../../core/constants/subscription.const";
import {QBSubscription, QBSubscriptionOption} from "../../core/models/subscription.model";
import {AuthService} from "../../core/services/auth.service";
import {NotificationService} from "../../core/services/notification.service";
import {SettingsService} from "../../core/services/settings.service";
import {ISettingsDTO} from "../../shared/DTOs/ISettingsDTO";
import {EOrganizationType} from "../../shared/enums/EOrganizationType";
import {EAttemptPaymentStatus} from "../../shared/enums/EAttemptPaymentStatus";

@Component({
  selector: "app-subscription-upgrade",
  templateUrl: "./subscription-upgrade.component.html",
  styleUrls: ["./subscription-upgrade.component.scss"],
})
export class SubscriptionUpgradeComponent implements OnInit {
  subscriptionOptions: QBSubscriptionOption[] = [];
  selectedSubscription: QBSubscriptionOption;
  accountTypeLabel = "Performance";
  participantLabel = "athletes";
  isLoading = false;
  isApplying = false;
  isCompleting = false;
  showPaymentForm = false;
  paymentError: string = null;
  hasPaymentMethod = false;
  requiresPaymentMethodUpdate = false;
  isPaymentFormBusy = false;

  private accountSubscriptionType = SubscriptionUserType.Performance;

  constructor(
    private accountControllerService: AccountControllerService,
    private shopControllerService: ShopControllerService,
    private notificationService: NotificationService,
    private settingsService: SettingsService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (!AuthService.getOauthTokenFromStorage()) {
      this.router.navigate(["/login"]);
      return;
    }

    this.isLoading = true;
    this.settingsService.settings$.pipe(
      filter((settings): settings is ISettingsDTO => settings !== null),
      take(1),
      switchMap((settings) => {
        this.applySettings(settings);
        return this.shopControllerService.getRegisterProducts();
      }),
    ).subscribe({
      next: (response: { subscriptions: QBSubscription }) => {
        const qbSubscriptions = new QBSubscription(response.subscriptions);
        const subscriptionType = this.accountSubscriptionType === SubscriptionUserType.Clinic
          ? qbSubscriptions.clinic
          : qbSubscriptions.performance;

        this.subscriptionOptions = subscriptionType.options.filter(
          (subscription) => subscription.price > 0,
        );
        this.isLoading = false;

        if (!this.subscriptionOptions.length) {
          this.paymentError = "No paid subscriptions are currently available for your account type.";
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.paymentError = "An error occurred while retrieving the subscriptions.";
        this.notificationService.error(error);
      },
    });
  }

  selectSubscription(subscription: QBSubscriptionOption): void {
    if (this.isBusy) {
      return;
    }

    this.subscriptionOptions.forEach((option) => option.selected = option.id === subscription.id);
    this.selectedSubscription = subscription;
    this.paymentError = this.requiresPaymentMethodUpdate ? paymentMethodUnavailableMessage : null;
    this.showPaymentForm = this.requiresPaymentMethodUpdate;
  }

  applySubscription(): void {
    if (!this.selectedSubscription || this.isBusy) {
      return;
    }

    if (!this.hasPaymentMethod || this.requiresPaymentMethodUpdate) {
      this.showPaymentForm = true;
      return;
    }

    this.isApplying = true;
    this.paymentError = null;
    this.showPaymentForm = false;

    this.accountControllerService.attemptPayment(this.attemptPaymentPayload).subscribe({
      next: (response) => this.handlePaymentStatus(response),
      error: (error) => this.handlePaymentFailure(error?.error, error?.error?.message),
    });
  }

  handlePaymentSucceeded(): void {
    this.finishUpgrade();
  }

  handlePaymentFailed(response?: IAttemptPaymentResponse): void {
    this.showPaymentForm = true;
    if (requiresPaymentMethodUpdate(response?.failureReason)) {
      this.hasPaymentMethod = false;
      this.requiresPaymentMethodUpdate = true;
      this.paymentError = paymentMethodUnavailableMessage;
      return;
    }

    this.paymentError = "The payment has failed. Retry the payment or update the card details below.";
  }

  handlePaymentMethodSaved(): void {
    this.hasPaymentMethod = true;
    this.requiresPaymentMethodUpdate = false;
    this.paymentError = null;
  }

  get isBusy(): boolean {
    return this.isApplying || this.isCompleting || this.isPaymentFormBusy;
  }

  get attemptPaymentPayload(): IAttemptPaymentRequest {
    return {
      subscriptionId: this.selectedSubscription?.id ?? null,
      isUpdate: true,
    };
  }

  private applySettings(settings: ISettingsDTO): void {
    this.accountSubscriptionType = settings.organization?.accountType === EOrganizationType.CLINIC
      ? SubscriptionUserType.Clinic
      : SubscriptionUserType.Performance;
    this.accountTypeLabel = this.accountSubscriptionType === SubscriptionUserType.Clinic ? "Clinic" : "Performance";
    this.participantLabel = this.accountSubscriptionType === SubscriptionUserType.Clinic ? "patients" : "athletes";
    this.hasPaymentMethod = settings.subscription?.hasPaymentMethod ?? false;
  }

  private handlePaymentStatus(response: IAttemptPaymentResponse): void {
    this.isApplying = false;
    if (response.status === EAttemptPaymentStatus.Paid || response.status === EAttemptPaymentStatus.NotRequired) {
      this.notificationService.success("The subscription has been upgraded succesfully!");
      this.finishUpgrade();
      return;
    }

    this.handlePaymentFailure(response);
  }

  private handlePaymentFailure(response?: IAttemptPaymentResponse, message?: string): void {
    this.isApplying = false;
    this.handlePaymentFailed(response);
    if (message && !this.requiresPaymentMethodUpdate) {
      this.paymentError = message;
    }
    this.notificationService.error("The payment has failed.");
  }

  private finishUpgrade(): void {
    this.isCompleting = true;
    this.accountControllerService.getUserSettings(false).subscribe({
      next: () => this.router.navigateByUrl("/"),
      error: (error) => {
        this.isCompleting = false;
        this.notificationService.error(error);
      },
    });
  }
}
